/**
 * Locations API Client
 *
 * Handles HTTP communication with the locations data endpoint (S3 JSON).
 * Provides error handling, response validation, and type-safe data fetching.
 *
 * @module locations-api-client
 */

import type { ApiLocationsResponse } from './location-transformer';

/**
 * Fetch locations data from the configured URL
 *
 * @param url - S3 JSON endpoint URL
 * @returns Raw API response (array or object format)
 * @throws Error if URL is invalid, network fails, or response is invalid
 *
 * @remarks
 * Uses native fetch API with explicit headers.
 * Validates response status and JSON parsing.
 * Does NOT transform data - returns raw API format for flexibility.
 */
export async function fetchLocationsData(
  url: string
): Promise<ApiLocationsResponse> {
  if (!url) {
    throw new Error('Locations URL is required');
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch locations: ${response.statusText}`);
  }

  const data = await response.json();

  // Validate response format (array or object with tours/markets)
  if (!Array.isArray(data) && !data.tours && !data.markets) {
    throw new Error(
      'Invalid API response format: expected array or {tours, markets} object'
    );
  }

  return data as ApiLocationsResponse;
}

/**
 * Locations API Client Service
 *
 * @remarks
 * Singleton pattern for consistency with other services (map-theme-service).
 * Provides namespaced API methods for future expansion (retry, caching, etc.).
 */
export class LocationsApiClient {
  /**
   * Fetch locations from the configured endpoint
   */
  async fetchLocations(url: string): Promise<ApiLocationsResponse> {
    return fetchLocationsData(url);
  }
}

export const locationsApiClient = new LocationsApiClient();
